import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiBell,
  FiCheckCircle,
  FiClock,
  FiDownload,
  FiEdit3,
  FiGrid,
  FiLogIn,
  FiMonitor,
  FiSmartphone,
  FiTable,
  FiUserPlus,
  FiUsers,
} from "react-icons/fi";
import PhoneMockup from "../../components/landing/PhoneMockup";
import SectionHeading from "../../components/landing/SectionHeading";
import StoreButtons from "../../components/landing/StoreButtons";
import CountUp from "../../components/landing/CountUp";
import useInView from "../../components/landing/useInView";
import "./platforms.css";

const MOBILE = [
  { Icon: FiCheckCircle, text: "Gün içinde hedeflerini birkaç dokunuşla işaretle" },
  { Icon: FiBell, text: "Hatırlatma ve grup bildirimleri telefonuna gelsin" },
  { Icon: FiClock, text: "Odak kipiyle okumanı süreli yap, kendiliğinden kaydedilsin" },
  { Icon: FiEdit3, text: "Latin ya da Osmanlıca harflerle kullan" },
];

const WEB = [
  { Icon: FiUsers, text: "Gruplarını ve üyelerin hedeflerini geniş ekranda yönet" },
  { Icon: FiTable, text: "Grup ve kişisel istatistikleri tablolarla incele" },
  { Icon: FiGrid, text: "Hatim şablonlarını ve dağıtımları rahatça düzenle" },
  { Icon: FiLogIn, text: "Kurulum yok: tarayıcıdan giriş yapman yeter" },
];

const STEPS = [
  { Icon: FiDownload, title: "İndir ya da gir", desc: "Uygulamayı mağazadan indir veya web'den giriş yap." },
  { Icon: FiUserPlus, title: "Hesabını aç", desc: "E-postanla kaydol; aynı hesap her yerde geçerli." },
  { Icon: FiCheckCircle, title: "Hedeflerini kur", desc: "Kendi hedeflerini ekle ya da grubunun koduyla katıl." },
];

const BARS = [60, 80, 45, 95, 70, 100, 85];
const MEMBERS = [
  { name: "Ahmet", pct: 100 },
  { name: "Mehmet", pct: 96 },
  { name: "Yusuf", pct: 88 },
  { name: "Ömer", pct: 81 },
];

/**
 * Görünür alana girince oynayan animasyon için sınıf + gecikme. Girmeden önce
 * öğe saydamdır; useInView gözlemci tetiklenmese de sonunda açar.
 */
function reveal(on, animation, delay = 0) {
  return {
    className: on ? `${animation} motion-reduce:animate-none` : "opacity-0",
    style: on && delay ? { animationDelay: `${delay}s` } : undefined,
  };
}

/** Çizgili özellik listesi: satırlar sırayla yükselir, simgeler hafifçe zıplar. */
function FeatureList({ items }) {
  const [ref, inView] = useInView({ threshold: 0.2 });
  return (
    <ul ref={ref} className="divide-y divide-terakkiBrandMist border-y border-terakkiBrandMist">
      {items.map(({ Icon, text }, i) => {
        const row = reveal(inView, "animate-rise-in", 0.1 + i * 0.12);
        const icon = reveal(inView, "animate-pop-in", 0.25 + i * 0.12);
        return (
          <li key={text} className={`group flex items-center gap-4 py-4 ${row.className}`} style={row.style}>
            <span
              className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-terakkiBrandCloud text-terakkiPrimaryDark transition-colors duration-300 group-hover:bg-terakkiBrandMist ${icon.className}`}
              style={icon.style}
            >
              <Icon size={18} />
            </span>
            <span className="text-terakkiInk/80 transition-colors duration-300 group-hover:text-terakkiInk">
              {text}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/** Hero'daki platform yongası: arkasında yavaşça nefes alan bir ışık. */
function PlatformChip({ Icon, label, delay }) {
  return (
    <div className="relative animate-pop-in motion-reduce:animate-none" style={{ animationDelay: `${delay}s` }}>
      <span
        aria-hidden
        className="pf-glow absolute -inset-1.5 rounded-full bg-terakkiPrimary/30 blur-md"
        style={{ animationDelay: `${delay}s` }}
      />
      <span className="relative flex items-center gap-2 rounded-full border border-terakkiBrandMist bg-white px-4 py-2 text-sm text-terakkiPrimaryDark shadow-sm">
        <Icon size={16} />
        {label}
      </span>
    </div>
  );
}

/** Mobil ile web arasında gidip gelen veri: noktalı akış ve iki yönlü nokta. */
function SyncLine() {
  return (
    <div
      aria-hidden
      className="relative h-6 w-16 sm:w-32 animate-hero-in [animation-delay:.55s] motion-reduce:animate-none"
    >
      <svg className="absolute inset-0 h-full w-full overflow-visible" preserveAspectRatio="none">
        <line
          x1="0"
          y1="50%"
          x2="100%"
          y2="50%"
          className="pf-dash stroke-terakkiPrimary/60"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray="2 10"
        />
      </svg>
      <div className="pf-travel absolute inset-0">
        <span className="absolute left-0 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-terakkiPrimary shadow-[0_0_0_4px_rgba(72,166,167,.18)]" />
      </div>
      <div className="pf-travel-back absolute inset-0">
        <span className="absolute left-0 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-terakkiPrimaryDark shadow-[0_0_0_4px_rgba(0,106,113,.15)]" />
      </div>
    </div>
  );
}

/** Telefonun çevresinde süzülen küçük etiketler. */
const PHONE_CHIPS = [
  { label: "Hatırlatma", Icon: FiBell, pos: "-right-2 top-10", delay: 1.45, float: "0s" },
  { label: "Osmanlıca", Icon: FiEdit3, pos: "-left-4 top-[48%]", delay: 1.65, float: "2s" },
  { label: "Odak 25 dk", Icon: FiClock, pos: "-left-1 bottom-6", delay: 1.85, float: "1s" },
];

function MobilePhone() {
  return (
    <div className="relative mx-auto w-full max-w-[380px]">
      <PhoneMockup />
      {PHONE_CHIPS.map(({ label, Icon, pos, delay, float }) => (
        <div
          key={label}
          aria-hidden
          className={`absolute ${pos} hidden sm:block animate-pop-in motion-reduce:animate-none`}
          style={{ animationDelay: `${delay}s` }}
        >
          <div
            className="flex items-center gap-1.5 rounded-full border border-white/70 bg-white/90 px-3 py-1.5 text-xs text-terakkiPrimaryDark shadow-lg shadow-terakkiPrimaryDark/10 backdrop-blur animate-float-slow motion-reduce:animate-none"
            style={{ animationDelay: float }}
          >
            <Icon size={13} />
            {label}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Elle çizilmiş tarayıcı: görünür olunca kayarak gelir, çubuklar büyür, üyeler sırayla belirir. */
function BrowserMock() {
  const [ref, inView] = useInView({ threshold: 0.3 });
  const frame = reveal(inView, "pf-slide-in");
  return (
    <div ref={ref} className="relative" aria-hidden>
      <div className="absolute -inset-8 rounded-full bg-terakkiPrimary/20 blur-3xl" />
      <div
        className={`relative overflow-hidden rounded-2xl border border-white/80 bg-white shadow-2xl shadow-terakkiPrimaryDark/20 ${frame.className}`}
      >
        <div className="flex items-center gap-1.5 border-b border-terakkiBorder bg-terakkiBrandCloud px-4 py-3">
          <span className="h-2.5 w-2.5 rounded-full bg-[#F16B5F]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#F7C14C]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#5FC454]" />
          <span className="ml-3 flex min-w-0 flex-1 items-center rounded-md bg-white px-3 py-1 text-[11px] text-terakkiNeutral">
            <span className="truncate">adimadimterakki.com/uygulama</span>
            <span className="pf-blink ml-0.5 inline-block h-3 w-px flex-shrink-0 bg-terakkiPrimaryDark" />
          </span>
          <span className="ml-2 flex items-center gap-1.5 text-[10px] text-terakkiPrimaryDark">
            <span className="relative flex h-2 w-2">
              <span className="pf-pulse absolute inset-0 rounded-full bg-terakkiPrimary" />
              <span className="relative h-2 w-2 rounded-full bg-terakkiPrimary" />
            </span>
            canlı
          </span>
        </div>
        <div className="grid grid-cols-[72px_1fr] sm:grid-cols-[88px_1fr]">
          <div className="space-y-2 border-r border-terakkiBorder bg-terakkiBrandCloud/60 p-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className={`h-2.5 rounded ${i === 2 ? "bg-terakkiPrimary" : "bg-terakkiBrandMist"}`} />
            ))}
          </div>
          <div className="space-y-4 p-4 sm:p-5">
            <div className="flex items-end justify-between gap-3">
              <div>
                <div className="text-[11px] text-terakkiNeutral">Grup haftası</div>
                <div className="text-2xl font-SemiBold text-terakkiInk">
                  %<CountUp value="92" start={inView} />
                </div>
              </div>
              <div className="flex h-12 items-end gap-1.5">
                {BARS.map((h, i) => {
                  const bar = reveal(inView, "pf-bar", 0.35 + i * 0.08);
                  return (
                    <div
                      key={i}
                      className={`w-2.5 rounded-t sm:w-3 ${i === 5 ? "bg-terakkiPrimaryDark" : "bg-terakkiPrimary/70"} ${bar.className}`}
                      style={{ height: `${h}%`, ...bar.style }}
                    />
                  );
                })}
              </div>
            </div>
            <div className="divide-y divide-terakkiBorder border-y border-terakkiBorder">
              {MEMBERS.map(({ name, pct }, i) => {
                const row = reveal(inView, "animate-rise-in", 0.6 + i * 0.14);
                const line = reveal(inView, "pf-draw-x", 0.8 + i * 0.14);
                return (
                  <div
                    key={name}
                    className={`flex items-center gap-3 py-2 text-[12px] ${row.className}`}
                    style={row.style}
                  >
                    <span className="w-14 text-terakkiInk">{name}</span>
                    <span className="h-1 flex-1 overflow-hidden rounded-full bg-terakkiBrandCloud">
                      <span
                        className={`block h-full rounded-full bg-terakkiPrimary/70 ${line.className}`}
                        style={{ width: `${pct}%`, ...line.style }}
                      />
                    </span>
                    <span className="w-9 text-right text-terakkiPrimaryDark">%{pct}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Üç adım: bağlantı çizgisi kendini çizer, simgeler sırayla belirir. */
function Steps() {
  const [ref, inView] = useInView({ threshold: 0.3 });
  const lineX = reveal(inView, "pf-draw-x", 0.1);
  const lineY = reveal(inView, "pf-draw-y", 0.1);
  return (
    <ol ref={ref} className="relative grid grid-cols-1 gap-8 md:grid-cols-3 md:gap-10">
      {/* Geniş ekranda yatay, dar ekranda dikey bağlantı çizgisi */}
      <span aria-hidden className="absolute left-[16.66%] right-[16.66%] top-7 hidden h-px bg-terakkiBrandMist md:block">
        <span
          className={`block h-full w-full bg-gradient-to-r from-terakkiPrimary to-terakkiPrimaryDark ${lineX.className}`}
          style={lineX.style}
        />
      </span>
      <span aria-hidden className="absolute bottom-10 left-7 top-7 w-px bg-terakkiBrandMist md:hidden">
        <span
          className={`block h-full w-full bg-gradient-to-b from-terakkiPrimary to-terakkiPrimaryDark ${lineY.className}`}
          style={lineY.style}
        />
      </span>

      {STEPS.map(({ Icon, title, desc }, index) => {
        const icon = reveal(inView, "animate-pop-in", 0.15 + index * 0.45);
        const text = reveal(inView, "animate-rise-in", 0.3 + index * 0.45);
        return (
          <li key={title} className="relative flex items-start gap-5 md:block md:space-y-3 md:text-center">
            <div
              className={`relative flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-terakkiPrimary to-terakkiPrimaryDark text-white shadow-lg shadow-terakkiPrimary/30 md:mx-auto ${icon.className}`}
              style={icon.style}
            >
              <Icon size={22} />
            </div>
            <div className={`space-y-1.5 pt-1 md:space-y-3 md:pt-0 ${text.className}`} style={text.style}>
              <div className="text-xs text-terakkiNeutral">{index + 1}. adım</div>
              <h3 className="text-lg font-SemiBold">{title}</h3>
              <p className="max-w-xs text-sm leading-relaxed text-terakkiInk/70 md:mx-auto">{desc}</p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/** Bölüm başlığı (simge + üst etiket + başlık), kaydırınca sırayla belirir. */
function PlatformHeading({ Icon, eyebrow, title }) {
  const [ref, inView] = useInView({ threshold: 0.3 });
  const top = reveal(inView, "animate-hero-in");
  const head = reveal(inView, "animate-hero-in", 0.12);
  return (
    <div ref={ref} className="space-y-4">
      <div className={`flex items-center gap-3 text-terakkiPrimaryDark ${top.className}`} style={top.style}>
        <Icon size={22} />
        <span className="text-xs uppercase tracking-[0.22em]">{eyebrow}</span>
      </div>
      <h2 className={`text-3xl font-SemiBold leading-tight md:text-4xl ${head.className}`} style={head.style}>
        {title}
      </h2>
    </div>
  );
}

function RevealBlock({ children, delay = 0.2, className = "" }) {
  const [ref, inView] = useInView({ threshold: 0.2 });
  const r = reveal(inView, "animate-rise-in", delay);
  return (
    <div ref={ref} className={`${className} ${r.className}`} style={r.style}>
      {children}
    </div>
  );
}

/** Mobil ve Web: iki yüzün neyi iyi yaptığı, nasıl başlanır, indirme. */
export default function Platforms() {
  return (
    <main className="w-full overflow-x-hidden text-terakkiInk">
      {/* Giriş */}
      <section className="mx-auto max-w-4xl space-y-6 px-6 pb-16 pt-4 text-center">
        <p className="animate-hero-in text-xs uppercase tracking-[0.22em] text-terakkiPrimaryDark/70 motion-reduce:animate-none">
          Mobil ve web
        </p>
        <h1 className="animate-hero-in text-4xl font-SemiBold leading-tight tracking-tight [animation-delay:.1s] motion-reduce:animate-none md:text-5xl">
          Mobilde ve web'de{" "}
          <span className="animate-hero-gradient bg-gradient-to-r from-terakkiPrimary via-terakkiPrimaryDark to-terakkiPrimary bg-[length:200%_auto] bg-clip-text text-transparent motion-reduce:animate-none">
            yanınızda
          </span>
        </h1>
        <p className="mx-auto max-w-2xl animate-hero-in text-lg leading-relaxed text-terakkiInk/70 [animation-delay:.2s] motion-reduce:animate-none">
          Hesabınız ve tüm kayıtlarınız her iki yerde de aynı. Gün içinde telefondan işaretleyin,
          grubunuzu bilgisayardan yönetin.
        </p>
        <div className="flex flex-col items-center gap-3 pt-2">
          <div className="flex items-center gap-3 sm:gap-4">
            <PlatformChip Icon={FiSmartphone} label="Mobil" delay={0.35} />
            <SyncLine />
            <PlatformChip Icon={FiMonitor} label="Web" delay={0.5} />
          </div>
          <p className="animate-hero-in text-xs text-terakkiNeutral [animation-delay:.7s] motion-reduce:animate-none">
            Aynı hesap, her yerde · kayıtlarınız iki yanda da aynı
          </p>
        </div>
      </section>

      {/* Mobil */}
      <section className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-14 px-6 pb-24 md:grid-cols-2">
        <MobilePhone />
        <div className="space-y-7">
          <PlatformHeading Icon={FiSmartphone} eyebrow="Mobil uygulama" title="Her an cebinde" />
          <FeatureList items={MOBILE} />
          <RevealBlock delay={0.6}>
            <StoreButtons />
          </RevealBlock>
        </div>
      </section>

      {/* Web */}
      <section className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-14 px-6 pb-24 md:grid-cols-2">
        <div className="order-2 space-y-7 md:order-1">
          <PlatformHeading Icon={FiMonitor} eyebrow="Web" title="Geniş ekranda yönetim" />
          <FeatureList items={WEB} />
          <RevealBlock delay={0.6}>
            <Link
              to="/giris"
              className="group inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-terakkiPrimary to-terakkiPrimaryDark px-5 py-3 font-SemiBold text-white shadow-lg shadow-terakkiPrimary/30 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-terakkiPrimary/40"
            >
              Web'den giriş yap
              <FiArrowRight className="transition-transform group-hover:translate-x-1" />
            </Link>
          </RevealBlock>
        </div>
        <div className="order-1 md:order-2">
          <BrowserMock />
        </div>
      </section>

      {/* Nasıl başlanır */}
      <section className="border-t border-terakkiBrandMist bg-white/50 backdrop-blur">
        <div className="mx-auto max-w-6xl space-y-12 px-6 py-20">
          <RevealBlock delay={0}>
            <SectionHeading eyebrow="Nasıl başlanır" title="Üç adımda hazırsın" center />
          </RevealBlock>
          <Steps />
          <RevealBlock delay={0.2} className="flex justify-center">
            <StoreButtons />
          </RevealBlock>
        </div>
      </section>
    </main>
  );
}
